import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import { BarometerNav } from "@/components/BarometerNav";
import { CrossSelling } from "@/components/CrossSelling";
import { generateWettbewerbPdf } from "@/lib/generatePdf";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";

const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

const C = {
  accent: "#7c2d12",
  accentDark: "#5a1f0a",
  accentLight: "#7c2d1212",
  teal: "#115751",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const DIMENSIONS = [
  "SEO-Grundlagen",
  "Vertrauen & Trust",
  "Content-Qualität",
  "Conversion",
  "Mobile & Speed",
  "Social Proof",
  "Branding",
  "Technik",
];

// Farben für bis zu 4 Websites im Radar-Chart
const SITE_COLORS = [
  { border: "#7c2d12", bg: "rgba(124,45,18,0.15)", label: "Deine Website" },
  { border: "#115751", bg: "rgba(17,87,81,0.15)", label: "Wettbewerber 1" },
  { border: "#b45309", bg: "rgba(180,83,9,0.15)", label: "Wettbewerber 2" },
  { border: "#7c3aed", bg: "rgba(124,58,237,0.15)", label: "Wettbewerber 3" },
];

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    { label: "Deine Website analysieren", icon: "/manus-storage/23_1ab7147e.svg" },
    { label: "Wettbewerber identifizieren", icon: "/manus-storage/icon_eeat_expertise_60d84526.png" },
    { label: "Wettbewerber-Websites crawlen", icon: "/manus-storage/41_3269a7aa.svg" },
    { label: "SEO-Grundlagen vergleichen", icon: "/manus-storage/33_d51b7819.svg" },
    { label: "Vertrauen & Trust-Signale messen", icon: "/manus-storage/29_7f3bb394.svg" },
    { label: "Content-Qualität bewerten", icon: "/manus-storage/icon_content_lesbarkeit_9425589a.png" },
    { label: "Conversion-Elemente prüfen", icon: "/manus-storage/icon_cta_value_9a48b0a2.png" },
    { label: "Wettbewerbs-Score berechnen", icon: "/manus-storage/32_04885248.svg" },
  ];
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % steps.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div className="w-full max-w-md text-center">
        <img src="/manus-storage/41_3269a7aa.svg" alt="" className="w-12 h-12 mb-6 object-contain mx-auto" style={{ opacity: 0.85 }} />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent, letterSpacing: "0.05em" }}>
          WETTBEWERBS-ANALYSE LÄUFT
        </h2>
        <p className="text-sm mb-8" style={{ color: C.muted }}>
          Analysiere <strong>{url}</strong> im Vergleich zu deinen Wettbewerbern
        </p>
        <div className="space-y-3 text-left">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-3 p-3 transition-all duration-500"
              style={{ background: i === activeStep ? C.accentLight : "transparent", border: `1px solid ${i === activeStep ? C.accent : "transparent"}` }}>
              <img src={step.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
              <span className="text-sm font-medium" style={{ color: i === activeStep ? C.accent : C.muted }}>
                {step.label}
              </span>
              {i < activeStep && <span className="ml-auto text-green-500 text-sm">✓</span>}
              {i === activeStep && <span className="ml-auto text-xs animate-pulse" style={{ color: C.accent }}>läuft…</span>}
            </div>
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: C.muted }}>
          Bitte warte – die Analyse dauert ca. 30–60 Sekunden
        </p>
      </div>
    </div>
  );
}

// ── Radar-Chart Komponente (Chart.js) ─────────────────────────────────────────
interface RadarChartProps {
  ownScores: number[];
  competitorScores?: number[][];
  competitorLabels?: string[];
  blurred?: boolean;
}

function RadarChart({ ownScores, competitorScores = [], competitorLabels = [], blurred = false }: RadarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    const loadChart = async () => {
      const ChartModule = await import("chart.js/auto");
      const Chart = ChartModule.default;

      if (!canvasRef.current) return;
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      const datasets = [
        {
          label: "Deine Website",
          data: ownScores,
          borderColor: SITE_COLORS[0].border,
          backgroundColor: SITE_COLORS[0].bg,
          borderWidth: 2.5,
          pointBackgroundColor: SITE_COLORS[0].border,
          pointRadius: 4,
        },
        ...competitorScores.map((scores, i) => ({
          label: competitorLabels[i] || `Wettbewerber ${i + 1}`,
          data: scores,
          borderColor: SITE_COLORS[i + 1]?.border || "#888",
          backgroundColor: SITE_COLORS[i + 1]?.bg || "rgba(136,136,136,0.1)",
          borderWidth: 2,
          pointBackgroundColor: SITE_COLORS[i + 1]?.border || "#888",
          pointRadius: 3,
          borderDash: [4, 4],
        })),
      ];

      chartRef.current = new Chart(canvasRef.current, {
        type: "radar",
        data: {
          labels: DIMENSIONS,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: { family: "'DM Sans', sans-serif", size: 11 },
                color: C.text,
                padding: 12,
              },
            },
            tooltip: {
              callbacks: {
                label: (ctx: any) => `${ctx.dataset.label}: ${ctx.raw}/100`,
              },
            },
          },
          scales: {
            r: {
              min: 0,
              max: 100,
              ticks: {
                stepSize: 25,
                font: { size: 9 },
                color: C.muted,
                backdropColor: "transparent",
              },
              grid: { color: C.border },
              angleLines: { color: C.border },
              pointLabels: {
                font: { family: "'DM Sans', sans-serif", size: 10, weight: 600 as any },
                color: C.text,
              },
            },
          },
        },
      });
    };

    loadChart();
    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [ownScores, competitorScores, competitorLabels]);

  return (
    <div style={{ position: "relative" }}>
      <div style={{ height: "320px", filter: blurred ? "blur(6px)" : "none", pointerEvents: blurred ? "none" : "auto" }}>
        <canvas ref={canvasRef} />
      </div>
      {blurred && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: "8px",
        }}>
          <img src="/manus-storage/29_7f3bb394.svg" alt="" className="w-8 h-8 object-contain" style={{ opacity: 0.4 }} />
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem", color: C.accent, letterSpacing: "0.05em" }}>
            VOLLVERSION FREISCHALTEN
          </div>
          <div style={{ fontSize: "0.75rem", color: C.muted }}>Detaillierter Radar-Vergleich aller Websites</div>
        </div>
      )}
    </div>
  );
}

// ── Vergleichstabelle ─────────────────────────────────────────────────────────
interface ComparisonTableProps {
  ownUrl: string;
  ownScores: number[];
  competitorScores: number[][];
  competitorLabels: string[];
  blurred?: boolean;
}

function ComparisonTable({ ownUrl, ownScores, competitorScores, competitorLabels, blurred = false }: ComparisonTableProps) {
  const allSites = [
    { label: ownUrl, scores: ownScores, color: SITE_COLORS[0].border, isOwn: true },
    ...competitorScores.map((scores, i) => ({
      label: competitorLabels[i] || `Wettbewerber ${i + 1}`,
      scores,
      color: SITE_COLORS[i + 1]?.border || "#888",
      isOwn: false,
    })),
  ];

  const getWinner = (dimIdx: number) => {
    let best = -1, bestScore = -1;
    allSites.forEach((site, i) => {
      if ((site.scores[dimIdx] || 0) > bestScore) {
        bestScore = site.scores[dimIdx] || 0;
        best = i;
      }
    });
    return best;
  };

  return (
    <div style={{ position: "relative", overflowX: "auto" }}>
      <div style={{ filter: blurred ? "blur(5px)" : "none", pointerEvents: blurred ? "none" : "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem", fontFamily: "'DM Sans', sans-serif" }}>
          <thead>
            <tr style={{ background: C.bgGray }}>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: C.text, borderBottom: `2px solid ${C.border}` }}>
                Dimension
              </th>
              {allSites.map((site, i) => (
                <th key={i} style={{
                  padding: "10px 12px", textAlign: "center", fontWeight: 700,
                  color: site.color, borderBottom: `2px solid ${C.border}`,
                  background: site.isOwn ? `${C.accent}08` : "transparent",
                }}>
                  {site.isOwn ? "⭐ Du" : `W${i}`}
                  <div style={{ fontSize: "0.65rem", color: C.muted, fontWeight: 400, maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {site.label.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0]}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DIMENSIONS.map((dim, dimIdx) => {
              const winner = getWinner(dimIdx);
              return (
                <tr key={dimIdx} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "8px 12px", fontWeight: 600, color: C.text }}>{dim}</td>
                  {allSites.map((site, siteIdx) => {
                    const score = site.scores[dimIdx] || 0;
                    const isWinner = siteIdx === winner;
                    return (
                      <td key={siteIdx} style={{
                        padding: "8px 12px", textAlign: "center",
                        background: site.isOwn ? `${C.accent}05` : "transparent",
                        fontWeight: isWinner ? 700 : 400,
                        color: isWinner ? site.color : C.muted,
                      }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                          <span>{score}</span>
                          <div style={{ width: "40px", height: "4px", background: C.border, borderRadius: "2px", overflow: "hidden" }}>
                            <div style={{ width: `${score}%`, height: "100%", background: site.color, borderRadius: "2px" }} />
                          </div>
                          {isWinner && <span style={{ fontSize: "0.6rem", color: site.color }}>★ Bester</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Gesamt-Zeile */}
            <tr style={{ background: C.bgGray, borderTop: `2px solid ${C.border}` }}>
              <td style={{ padding: "10px 12px", fontWeight: 700, color: C.text }}>Gesamt-Score</td>
              {allSites.map((site, i) => {
                const total = Math.round(site.scores.reduce((a, b) => a + b, 0) / site.scores.length);
                const isOverallWinner = i === allSites.reduce((best, s, idx) => {
                  const t = Math.round(s.scores.reduce((a, b) => a + b, 0) / s.scores.length);
                  const bestT = Math.round(allSites[best].scores.reduce((a, b) => a + b, 0) / allSites[best].scores.length);
                  return t > bestT ? idx : best;
                }, 0);
                return (
                  <td key={i} style={{
                    padding: "10px 12px", textAlign: "center",
                    fontWeight: 700, fontSize: "1rem",
                    color: isOverallWinner ? site.color : C.muted,
                    background: site.isOwn ? `${C.accent}08` : "transparent",
                  }}>
                    {total}
                    {isOverallWinner && <div style={{ fontSize: "0.65rem" }}>🏆 Sieger</div>}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      {blurred && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: "8px",
          background: "rgba(255,255,255,0.4)",
        }}>
          <img src="/manus-storage/29_7f3bb394.svg" alt="" className="w-8 h-8 object-contain" style={{ opacity: 0.4 }} />
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.1rem", color: C.accent, letterSpacing: "0.05em" }}>
            VOLLVERSION FREISCHALTEN
          </div>
          <div style={{ fontSize: "0.75rem", color: C.muted }}>Detaillierter Vergleich aller 8 Dimensionen</div>
        </div>
      )}
    </div>
  );
}

// ── Vollversion-Ergebnisansicht ────────────────────────────────────────────
interface WettbewerbSite {
  url: string;
  domain: string;
  title?: string;
  total_score: number;
  max_score: number;
  percent: number;
  grade: string;
  scores: Record<string, { label: string; score: number; max: number }>;
  is_own: boolean;
  label?: string;
}

interface FullWettbewerbData {
  url: string;
  domain?: string;
  mode: string;
  competitors_found: string[];
  websites: WettbewerbSite[];
  comparison?: Record<string, { label: string; own: number; avg_competitor: number; best_competitor: number; advantage: boolean; gap: number }>;
  own_staerken: Array<{ dimension: string; text: string }>;
  own_schwaechen: Array<{ dimension: string; text: string }>;
  optimierungen: Array<{ dimension: string; kriterium: string; empfehlung: string; prioritaet: string }>;
  own_rank: number;
  total_sites: number;
}

function FullWettbewerbResults({ data, onReset }: { data: FullWettbewerbData; onReset: () => void }) {
  const [activeTab, setActiveTab] = useState<"overview" | "comparison" | "recommendations">("overview");
  const ownSite = data.websites?.find(w => w.is_own) ?? data.websites?.[0];
  const competitors = data.websites?.filter(w => !w.is_own) ?? [];
  const pct = ownSite?.percent ?? 0;
  const grade = ownSite?.grade ?? "–";
  const gradeColor = grade === "A" ? "#16a34a" : grade === "B" ? "#2563eb" : grade === "C" ? "#d97706" : grade === "D" ? "#dc2626" : "#7c3aed";
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (pct / 100) * circumference;

  // Dimension-Scores aus den echten Analyzer-Daten
  const ownDimScores = DIMENSIONS.map(dim => {
    const key = Object.keys(ownSite?.scores ?? {}).find(k => (ownSite?.scores[k]?.label ?? k).toLowerCase().includes(dim.toLowerCase().split("-")[0]));
    if (key && ownSite?.scores[key]) {
      const s = ownSite.scores[key];
      return s.max > 0 ? Math.round((s.score / s.max) * 100) : 0;
    }
    return 0;
  });

  const competitorDimScores = competitors.map(site =>
    DIMENSIONS.map(dim => {
      const key = Object.keys(site.scores ?? {}).find(k => (site.scores[k]?.label ?? k).toLowerCase().includes(dim.toLowerCase().split("-")[0]));
      if (key && site.scores[key]) {
        const s = site.scores[key];
        return s.max > 0 ? Math.round((s.score / s.max) * 100) : 0;
      }
      return 0;
    })
  );

  const priorityLabel = (p: string) => {
    if (p === "kritisch") return { label: "Kritisch", color: "#dc2626" };
    if (p === "hoch") return { label: "Hoch", color: "#ea580c" };
    return { label: "Mittel", color: "#ca8a04" };
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/wettbewerb-barometer" accentColor={C.accent} />
      <div style={{ height: "4px", backgroundColor: C.accent }} />

      {/* Ergebnis-Hero */}
      <section className="bg-black px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="#1f2937" strokeWidth="12" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={gradeColor} strokeWidth="12"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  strokeLinecap="round" transform="rotate(-90 70 70)" />
                <text x="70" y="62" textAnchor="middle" fill="white" fontSize="28" fontWeight="900" fontFamily="'Bebas Neue', sans-serif">{pct}</text>
                <text x="70" y="82" textAnchor="middle" fill="#9ca3af" fontSize="13" fontFamily="'DM Sans', sans-serif">von 100</text>
              </svg>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent }}>Wettbewerbs-Barometer™ Vollversion</p>
              <h1 className="font-black uppercase text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3rem)" }}>{data.domain ?? data.url}</h1>
              <p className="text-gray-400 text-sm mb-4">
                Rang {data.own_rank} von {data.total_sites} · {ownSite?.total_score ?? 0} von {ownSite?.max_score ?? 100} Punkten
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: gradeColor, borderColor: gradeColor }}>{pct} Punkte</span>
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: C.accent, borderColor: C.accent }}>Rang #{data.own_rank} von {data.total_sites}</span>
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border border-gray-600 text-gray-400">{competitors.length} Wettbewerber analysiert</span>
              </div>
            </div>
            <button onClick={onReset} className="text-xs font-bold uppercase tracking-widest px-6 py-3 border border-gray-600 text-gray-300 hover:border-white hover:text-white transition-colors">
              Neue Analyse
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex">
          {(["overview", "comparison", "recommendations"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors border-b-2"
              style={{
                borderColor: activeTab === tab ? C.accent : "transparent",
                color: activeTab === tab ? C.accent : "#6b7280",
              }}>
              {tab === "overview" ? "Übersicht" : tab === "comparison" ? "Vergleich" : "Empfehlungen"}
            </button>
          ))}
        </div>
      </div>

      {/* Tab-Inhalt */}
      <div className="max-w-5xl mx-auto w-full px-6 py-10">
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Radar-Chart mit echten Daten */}
            <div>
              <h2 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.6rem" }}>RADAR-CHART: ALLE WEBSITES</h2>
              <RadarChart
                ownScores={ownDimScores}
                competitorScores={competitorDimScores}
                competitorLabels={competitors.map(c => c.domain ?? c.url)}
                blurred={false}
              />
            </div>

            {/* Stärken & Schwächen */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-green-200 p-6" style={{ background: "#f0fdf4" }}>
                <h3 className="font-black uppercase mb-4 text-green-800" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Deine Stärken</h3>
                <div className="space-y-2">
                  {data.own_staerken?.map((s, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <div>
                        <span className="text-xs font-bold text-green-700">{s.dimension}: </span>
                        <span className="text-xs text-green-700">{s.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-red-200 p-6" style={{ background: "#fef2f2" }}>
                <h3 className="font-black uppercase mb-4 text-red-800" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Rückstände</h3>
                <div className="space-y-2">
                  {data.own_schwaechen?.map((s, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <div>
                        <span className="text-xs font-bold text-red-700">{s.dimension}: </span>
                        <span className="text-xs text-red-700">{s.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "comparison" && (
          <div>
            <h2 className="font-black uppercase mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.6rem" }}>DETAILLIERTER WETTBEWERBER-VERGLEICH</h2>
            <ComparisonTable
              ownUrl={data.domain ?? data.url}
              ownScores={ownDimScores}
              competitorScores={competitorDimScores}
              competitorLabels={competitors.map(c => c.domain ?? c.url)}
              blurred={false}
            />
            {/* Vergleich-Tabelle pro Dimension */}
            {data.comparison && (
              <div className="mt-8 space-y-4">
                <h3 className="font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.2rem" }}>DIMENSIONSVERGLEICH</h3>
                {Object.entries(data.comparison).map(([key, comp]) => (
                  <div key={key} className="border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold" style={{ color: C.text }}>{comp.label}</span>
                      <span className="text-xs font-bold" style={{ color: comp.advantage ? "#16a34a" : "#dc2626" }}>
                        {comp.advantage ? `+${comp.gap} vor Ø` : `${comp.gap} hinter Ø`}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div><div className="text-lg font-black" style={{ color: C.accent }}>{comp.own}</div><div className="text-xs text-gray-400">Deine Website</div></div>
                      <div><div className="text-lg font-black text-gray-500">{comp.avg_competitor}</div><div className="text-xs text-gray-400">Ø Wettbewerber</div></div>
                      <div><div className="text-lg font-black" style={{ color: "#16a34a" }}>{comp.best_competitor}</div><div className="text-xs text-gray-400">Bester Wettbewerber</div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "recommendations" && (
          <div>
            <h2 className="font-black uppercase mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.6rem" }}>ALLE WETTBEWERBS-OPTIMIERUNGEN</h2>
            <div className="space-y-4">
              {(data.optimierungen ?? []).map((opt, i) => {
                const p = priorityLabel(opt.prioritaet);
                return (
                  <div key={i} className="border border-gray-200 p-5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: p.color }}>{p.label}</span>
                          <span className="text-xs text-gray-400">{opt.dimension}</span>
                        </div>
                        <p className="text-sm font-bold" style={{ color: C.text }}>{opt.kriterium}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 border-l-2 pl-3" style={{ borderColor: C.accent }}>{opt.empfehlung}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="border-t border-gray-200 py-12 px-6 text-center">
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent }}>Wettbewerbsvorteile ausbauen</p>
        <h2 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>Jetzt die Lücken schließen</h2>
        <p className="text-gray-500 text-sm max-w-lg mx-auto mb-6">Die identifizierten Wettbewerbs-Hebel können professionell umgesetzt werden – mit konkreten Maßnahmen, um Wettbewerber zu überholen.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => generateWettbewerbPdf(data as unknown as Record<string, unknown>)}
            className="text-white font-bold text-sm uppercase tracking-widest px-8 py-4 transition-colors"
            style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
            📄 PDF-Report herunterladen
          </button>
          <a href="https://www.werbeagentur-larissa-girenko.de/kontakt" target="_blank" rel="noopener noreferrer"
            className="inline-block text-white font-bold text-sm tracking-widest uppercase px-10 py-4"
            style={{ backgroundColor: C.accent }}>Beratungsgespräch anfragen →</a>
        </div>
      </section>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────────
export default function WettbewerbHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "Wettbewerbs-Barometer™ – Wo liegst du gegenüber deiner Konkurrenz? | WERBEAGENTUR Larissa Girenko",
    description: "Deine Website vs. bis zu 3 Wettbewerber in 8 Dimensionen. Wettbewerbs-Barometer™ zeigt genau, wo du vorne liegst und wo du aufholen musst. Ab 97 €.",
    path: "/wettbewerb-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const [competitorUrls, setCompetitorUrls] = useState(["", "", ""]);
  const [fullResult, setFullResult] = useState<FullWettbewerbData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = trpc.wettbewerbBarometer.analyze.useMutation({
    onSuccess: (res) => {
      if (res.success && res.data) {
        setFullResult(res.data as unknown as FullWettbewerbData);
      } else {
        setError((res as any).error || "Vollanalyse fehlgeschlagen");
      }
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || !isAdminUser) return;
    setError(null);
    const competitors = mode === "manual" ? competitorUrls.filter(u => u.trim().length > 0) : [];
    analyze.mutate({ url: url.trim(), competitorUrls: competitors.length > 0 ? competitors : undefined });
  };

  const handleReset = () => {
    setFullResult(null);
    setError(null);
    setUrl("");
    setCompetitorUrls(["", "", ""]);
  };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };
  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => { if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank"); },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({ origin: window.location.origin, productId: "wettbewerb_analyse", targetUrl: url.trim() || undefined, returnPath: "/wettbewerb-barometer" });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({ origin: window.location.origin, productId: "wettbewerb_mit_videocall", targetUrl: url.trim() || undefined, returnPath: "/wettbewerb-barometer" });
  };
  if (analyze.isPending) return <LoadingScreen url={url} />;
  if (fullResult) return <FullWettbewerbResults data={fullResult} onReset={handleReset} />;

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/wettbewerb-barometer" accentColor={C.accent} />

      {/* Hero – schwarzer Block mit Bild (wie Conversion-Barometer) */}
      <section className="w-full bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch" style={{ minHeight: "480px" }}>
          {/* Mobil: Titel über Bild */}
          <div className="block md:hidden px-8 pt-10 pb-6 z-10">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
              Wettbewerbs-Barometer™ · Werbeagentur Larissa Girenko
            </p>
            <h1 className="font-black uppercase leading-none mb-4 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.6rem, 10vw, 4rem)" }}>
              Wettbewerbs-<br />Barometer™
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Deine Website wird mit bis zu 3 Wettbewerbern in 8 Dimensionen verglichen – du siehst genau, wo du vorne liegst und wo du aufholen musst.
            </p>
          </div>

          {/* Bild */}
          <div className="w-full md:w-[42%] md:order-2 relative overflow-hidden" style={{ minHeight: "280px" }}>
            <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.6) 0%, transparent 60%)" }} />
            <div className="absolute top-0 left-0 right-0 h-1 z-20" style={{ backgroundColor: C.accent }} />
            <img
              src={PORTRAIT_IMG}
              alt="Larissa Girenko – Wettbewerbs-Expertin"
              className="w-full h-full object-cover object-top"
              style={{ minHeight: "280px" }}
            />
          </div>

          {/* Desktop: Text links */}
          <div className="hidden md:flex flex-1 flex-col justify-center px-8 md:px-16 py-14 md:order-1 z-10">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
              Wettbewerbs-Barometer™ · Werbeagentur Larissa Girenko
            </p>
            <h1 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.4rem, 5.5vw, 4rem)" }}>
              Wettbewerbs-<br />Barometer™
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed mb-8 max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Deine Website wird mit bis zu 3 Wettbewerbern in 8 Dimensionen verglichen – du siehst genau, wo du vorne liegst und wo du aufholen musst.
            </p>
            <div className="flex flex-wrap gap-5">
              <div className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>8 Dimensionen</p>
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Bis zu 3 Wettbewerber · Radar-Chart</p>
              </div>
              <div className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Auto-Erkennung</p>
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Kostenlose Vorschau · Vollversion 97 €</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vertrauens-Badge */}
      <div className="flex justify-center py-5 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-yellow-500 text-sm">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
            <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>5,0 Kundenbewertung</span>
          </div>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>17+ Jahre Erfahrung</span>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>Persoenliche Analyse von Larissa</span>
        </div>
      </div>

      {/* Lead-Magnet Hauptsektion */}
      <section className="max-w-3xl mx-auto w-full px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
            Persönliche Analyse von Larissa
          </p>
          <h2 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3.5rem)" }}>
            Ich analysiere das<br />fuer dich
          </h2>
          <p className="text-gray-500 text-sm max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Trag deine Website-URL ein und buche die Analyse direkt. Ich analysiere deine Wettbewerbsposition persönlich und zeige dir, wo du gegenüber deinen Konkurrenten stehst.
          </p>
        </div>

        {/* Was ich analysiere */}
        <div className="border border-gray-200 p-6 mb-8" style={{ backgroundColor: C.bgGray }}>
          <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Was ich fuer dich analysiere</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: "W", title: "Wettbewerber-Vergleich", desc: "Bis zu 3 Konkurrenten automatisch erkannt" },
              { icon: "S", title: "SEO-Staerken & -Luecken", desc: "Wo du vorne liegst, wo du aufholen musst" },
              { icon: "T", title: "Technische Qualitaet", desc: "Speed, Mobile, Core Web Vitals" },
              { icon: "C", title: "Content & Autoritat", desc: "E-E-A-T, Tiefe, Themenabdeckung" },
              { icon: "R", title: "Radar-Analyse", desc: "8 Dimensionen im direkten Vergleich" },
              { icon: "H", title: "Handlungsempfehlungen", desc: "Priorisierte Quick-Wins und Massnahmen" },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3 bg-white p-3 border border-gray-100">
                <span className="text-sm font-black flex-shrink-0 w-6 h-6 flex items-center justify-center" style={{ backgroundColor: C.accentLight, color: C.accent }}>{item.icon}</span>
                <div>
                  <p className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.title}</p>
                  <p className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Formular + CTA */}
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Deine Website-URL (optional)
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleBooking()}
              placeholder="https://deine-website.de"
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>

          {isAdminUser ? (
            <div className="space-y-3">
              <div className="border border-dashed border-gray-300 px-4 py-3 text-center">
                <p className="text-xs text-gray-500 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Admin-Modus: Vollanalyse fuer Kunden
                </p>
              </div>
              <button
                onClick={() => { if (!url.trim()) return; setError(null); const competitors = mode === "manual" ? competitorUrls.filter(u => u.trim().length > 0) : []; analyze.mutate({ url: url.trim(), competitorUrls: competitors.length > 0 ? competitors : undefined }); }}
                disabled={!url.trim() || analyze.isPending}
                className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-4 transition-opacity disabled:opacity-40"
                style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}
              >
                {analyze.isPending ? "Analysiert ..." : "Wettbewerbs-Analyse starten"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-bold tracking-widest uppercase text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{formatPrice(BAROMETER_PRICES.wettbewerb)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Social Proof */}
        <div className="mt-12 border-t border-gray-200 pt-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "Jetzt weiss ich endlich, warum Wettbewerber mehr Traffic bekommen.", name: "Klaus M., KMU" },
              { quote: "Der Vergleich hat uns 3 konkrete Quick-Wins gezeigt.", name: "Sandra P., Online-Shop" },
              { quote: "Larissa hat mir mehr Insights geliefert als teure SEO-Tools.", name: "Peter K., Agentur" },
            ].map((item, i) => (
              <div key={i} className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                <p className="text-sm italic text-gray-600 mb-2 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.quote}</p>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Cross-Selling */}
      <CrossSelling
        headline="Wettbewerb analysiert – was jetzt?"
        subline="Nutze diese Barometer um die Lücken gegenüber deinen Wettbewerbern gezielt zu schließen."
        items={[
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "100+ SEO-Kriterien – von Title-Tags bis E-E-A-T. Hol dir den SEO-Vorsprung vor deinen Wettbewerbern.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
            badge: "Top-Empfehlung",
          },
          {
            icon: "/manus-storage/33_d51b7819.svg",
            title: "Conversion-Barometer™",
            description: "Warum kaufen Besucher nicht? Psychologische Hebel für mehr Conversions als deine Konkurrenz.",
            price: formatPrice(BAROMETER_PRICES.conversion),
            href: "/conversion-barometer",
            accent: "#b45309",
          },
          {
            icon: "/manus-storage/11_094992a3.svg",
            title: "Sensorik-Barometer™",
            description: "Wie wirkt deine Website auf das Unterbewusstsein? Oft der entscheidende Unterschied zum Wettbewerber.",
            price: formatPrice(BAROMETER_PRICES.sensorik),
            href: "/sensorik",
            accent: "#7c2d12",
          },
        ]}
      />
    </div>
  );
}
